import React from 'react';
import {
    Box,
    Grid,
    Typography,
    Chip,
} from '@mui/material';
import { Vehicle } from '../../store/slices/vehicleSlice';
import GenericModal from '../../components/GenericModal';
import GenericButton from '../../components/GenericButton';

interface VehicleViewProps {
    open: boolean;
    vehicle: Vehicle;
    onClose: () => void;
}

const VehicleView: React.FC<VehicleViewProps> = ({ open, vehicle, onClose }) => {
    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const getStatusChip = () => {
        return <Chip label="Active" color="success" size="small" />;
    };

    return (
        <GenericModal
            open={open}
            onClose={onClose}
            title={vehicle.vehicle_number}
            subtitle={`Created: ${formatDateTime(vehicle.created_at)}`}
            actions={
                <GenericButton
                    variant="outline"
                    onClick={onClose}
                >
                    Close
                </GenericButton>
            }
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {getStatusChip()}
            </Box>

            {/* Basic Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Basic Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Branch
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.branch?.name || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Vehicle Type
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.type_of_vehicle || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Make
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.make || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Model
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.model || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Capacity Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Capacity Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Unladen Weight
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.unladen_weight || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Capacity (Units)
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.capacity_in_unit || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Capacity (Weight)
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.capacity_in_weight || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Capacity (Volume)
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.capacity_in_volume || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Technical Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Technical Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Chassis Number
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.chasis_number || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Engine Number
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.engine_number || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Marker Name
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.marker_name || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Registration Number
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.registration_number || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Validity Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Validity Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            PUC Validity
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {formatDate(vehicle.puc_validity)}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Insurance Validity
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {formatDate(vehicle.insurance_validity)}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Ownership Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Ownership Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Ownership
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.ownership || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Rented From
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {formatDate(vehicle.rented_from)}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Rented To
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {formatDate(vehicle.rented_to)}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Owner Name
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.owner_name || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Transporter
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.transporter || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Financer
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.financer || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* System Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    System Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Created By
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {vehicle.user?.name || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Last Updated
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {formatDateTime(vehicle.updated_at)}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        </GenericModal>
    );
};

export default VehicleView;
